// La campana (D52): en la barra de arriba junta lo que le importa a esta persona segun sus permisos (correos nuevos
// de sus buzones, clientes que respondieron, casos sin respuesta, seguimientos, reuniones proximas e invitaciones).
// Suena, salta y avisa aunque la Consola este en otra pestaña; 5 minutos antes de una reunion aparece una tarjeta con
// "Unirse" que no se va sola. Usa las mismas lecturas que ya hace la Consola (correo y casos cada minuto, citas cada 2).
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { type Cita, citasCreadasDesde, citasEntre } from '../api/citas';
import { useSession } from '../auth/session';
import { sonarAviso } from '../lib/audio';
import { filtrarVista } from '../lib/correo';
import { Icon } from '../lib/icons';
import {
  type Aviso,
  avisoDeHoras,
  avisosDeCasos,
  avisosDeCopias,
  avisosDeCorreos,
  avisosDeInvitaciones,
  avisosDeProyectos,
  avisosDeReuniones,
  enNoMolestar,
  esNuevo,
  filtrarPorPrefs,
  type Grupo,
  grupoDe,
  juntar,
  type Leidos,
  marcar,
  porAlertar,
  PREF_NOTIF,
  type PrefNotif,
  sonidoDe,
  type TipoAviso,
} from '../lib/notificaciones';
import { META_SEMANA, minutosDe, semanaDe } from '../lib/horas';
import { alertasProyecto } from '../lib/proyectos';
import { aplicarReglas, useReglas } from '../lib/reglas';
import { horasEntre } from '../api/proyectos';
import { useAvances } from './useAvances';
import { readJSON, writeJSON } from '../lib/storage';
import { conHistorial, useEstadosCorreo } from '../screens/bandeja/Correos';
import { useBuzonesVisibles, useHilos } from '../screens/bandeja/useHilos';
import { useCasosSinCerrar, useMisGrupos } from '../screens/bandeja/useVistas';
import { useCopiasRecientes, useDeshacerCopia } from './AvisoCopias';
import { abrirDialogo } from './forms';
import { TarjetaPermiso } from './Permisos';
import { toast } from './toasts';

/* ---------------- preferencias (Mi perfil) ---------------- */
export const CLAVE_PREF = 'consola.avisos';
export const leerPref = () => readJSON<PrefNotif>(CLAVE_PREF, PREF_NOTIF);
export const guardarPref = (p: PrefNotif) => {
  writeJSON(CLAVE_PREF, p);
  window.dispatchEvent(new Event('consola-avisos'));
};
function usePref() {
  const [p, setP] = useState(leerPref);
  useEffect(() => {
    const f = () => setP(leerPref());
    window.addEventListener('consola-avisos', f);
    window.addEventListener('storage', f);
    return () => {
      window.removeEventListener('consola-avisos', f);
      window.removeEventListener('storage', f);
    };
  }, []);
  return p;
}

/* ---------------- leidos, por persona ---------------- */
const claveLeidos = (miembroId: string) => `consola.notif.${miembroId}`;
function useLeidos(miembroId: string) {
  const [l, setL] = useState<Leidos>(() => {
    const v = readJSON<Leidos | null>(claveLeidos(miembroId), null);
    if (v && typeof v.desde === 'string') return { desde: v.desde, leidos: v.leidos ?? {} };
    const nuevo = { desde: new Date().toISOString(), leidos: {} };
    writeJSON(claveLeidos(miembroId), nuevo);
    return nuevo;
  });
  const cambiar = useCallback(
    (n: Leidos) => {
      setL(n);
      writeJSON(claveLeidos(miembroId), n);
    },
    [miembroId],
  );
  return [l, cambiar] as const;
}

// Reloj de la campana: cada 30 s (las reuniones cuentan minutos).
function useAhora() {
  const [t, setT] = useState(Date.now);
  useEffect(() => {
    const i = setInterval(() => setT(Date.now()), 30_000);
    return () => clearInterval(i);
  }, []);
  return t;
}

const DIEZ_MIN = 10 * 60_000;

function useAvisos(ahora: number, pref: PrefNotif) {
  const { yo } = useSession();
  const grupos = useMisGrupos();
  // Correos: la misma regla y cache que el contador de Correos (D38: solo buzones que esta persona puede leer).
  const buzones = useBuzonesVisibles();
  const cuentas = useMemo(() => (buzones.data ?? []).map((b) => b.id), [buzones.data]);
  const correos = useHilos(cuentas, 'entrada');
  const idsHilos = useMemo(() => correos.hilos.map((h) => h.id), [correos.hilos]);
  const { estados } = useEstadosCorreo(idsHilos);
  const reglas = useReglas();
  const mios = useMemo(() => (buzones.data ?? []).map((b) => b.correo.toLowerCase()), [buzones.data]);
  const noLeidos = useMemo(() => filtrarVista(correos.hilos, aplicarReglas(correos.hilos, conHistorial(correos.hilos, estados), reglas), mios, 'noleidos'), [correos.hilos, estados, reglas, mios]);
  const casos = useCasosSinCerrar();
  const copias = useCopiasRecientes();
  // Citas: de hace 15 min a mañana. La clave no cambia (se guarda en el navegador y no se pide de nuevo cada 10 min
  // por estrenar clave): la ventana se calcula al pedir, redondeada a 10 min.
  const proximas = useQuery({
    queryKey: ['citas', 'campana'],
    queryFn: () => {
      const tramo = Math.floor(Date.now() / DIEZ_MIN) * DIEZ_MIN;
      return citasEntre(new Date(tramo - 15 * 60_000), new Date(tramo + 25 * 3_600_000), 300);
    },
    refetchInterval: 120_000,
  });
  const creadas = useQuery({
    queryKey: ['citas', 'creadas'],
    queryFn: () => citasCreadasDesde(new Date(Math.floor(Date.now() / 3_600_000) * 3_600_000 - 3 * 86_400_000)),
    refetchInterval: 120_000,
    enabled: pref.invitaciones,
  });
  const citas = useMemo(() => {
    const m = new Map<string, Cita>();
    for (const c of [...(proximas.data ?? []), ...(creadas.data ?? [])]) m.set(c.id, c);
    return m;
  }, [proximas.data, creadas.data]);
  // Proyectos y horas (D96).
  const proy = useAvances();
  const lunes = semanaDe().desde.toISOString();
  const antes = semanaDe(Date.now() - 7 * 86_400_000);
  const pasada = useQuery({ queryKey: ['horas', 'semana', antes.dias[0]], queryFn: () => horasEntre(antes.desde, antes.hasta), staleTime: 30 * 60_000, enabled: !!yo.miembroId });
  const deProyectos = useMemo(
    () => avisosDeProyectos(proy.proyectos.map((p) => ({ id: p.id, nombre: p.nombre, responsableId: p.responsableId, alertas: proy.avances.get(p.id) ? alertasProyecto(proy.avances.get(p.id)!) : [] })), yo.miembroId, lunes),
    [proy.proyectos, proy.avances, yo.miembroId, lunes],
  );
  const deHoras = useMemo(
    () => {
      // Solo a quien trabaja en proyectos: responsable de uno activo o con horas la semana pasada.
      const suyas = (pasada.data ?? []).filter((r) => r.miembroId === yo.miembroId);
      const responsable = proy.proyectos.some((p) => p.estado === 'ACTIVO' && p.responsableId === yo.miembroId);
      return pasada.data && (suyas.length || responsable) ? avisoDeHoras(minutosDe(suyas), META_SEMANA, lunes, ahora) : [];
    },
    [pasada.data, proy.proyectos, yo.miembroId, lunes, ahora],
  );
  const avisos = useMemo(
    () =>
      filtrarPorPrefs(
        [
          ...deProyectos,
          ...deHoras,
          ...avisosDeReuniones(proximas.data ?? [], yo.miembroId, pref.reuniones, ahora),
          ...avisosDeCasos(casos.data ?? [], yo.miembroId, grupos.miembro, ahora),
          ...avisosDeCorreos(noLeidos, ahora),
          ...avisosDeInvitaciones(creadas.data ?? [], yo.miembroId, ahora),
          ...avisosDeCopias(copias.data ?? [], ahora),
        ],
        pref,
      ),
    [proximas.data, casos.data, noLeidos, creadas.data, copias.data, yo.miembroId, grupos.miembro, pref, ahora, deProyectos, deHoras],
  );
  const listo = !!casos.data && !!proximas.data && (cuentas.length === 0 || !!correos.data);
  return { avisos, citas, listo };
}

/* ---------------- la campana ---------------- */
const FILTROS = ['Todo', 'Sin leer', 'Correos', 'Casos', 'Reuniones', 'Proyectos'] as const;
type Filtro = (typeof FILTROS)[number];
const DE_FILTRO: Record<TipoAviso, Filtro> = { correo: 'Correos', cliente: 'Casos', sla: 'Casos', seguimiento: 'Casos', reunion: 'Reuniones', invitacion: 'Reuniones', equipo: 'Reuniones', copia: 'Casos', proyecto: 'Proyectos', horas: 'Proyectos' };
const ICONO: Record<TipoAviso, Parameters<typeof Icon>[0]['name']> = { correo: 'mail', cliente: 'reply', sla: 'clock', seguimiento: 'check', reunion: 'video', invitacion: 'calendar', equipo: 'users', copia: 'link', proyecto: 'task', horas: 'clock' };
const GRUPOS: [Grupo, string][] = [
  ['ahora', 'Ahora'],
  ['hoy', 'Hoy'],
  ['antes', 'Antes'],
];
const horaCorta = (fecha: string, ahora: number) => {
  const t = Date.parse(fecha);
  const min = Math.round((ahora - t) / 60_000);
  if (min < 1 && min > -1) return 'ahora';
  if (min > 0 && min < 60) return `hace ${min} min`;
  if (new Date(t).toDateString() === new Date(ahora).toDateString()) return new Date(t).toLocaleTimeString('es-CO', { hour: 'numeric', minute: '2-digit' });
  return new Date(t).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
};

export function Campana() {
  const { yo } = useSession();
  if (!yo.miembroId) return null;
  return <CampanaDe miembroId={yo.miembroId} />;
}

function CampanaDe({ miembroId }: { miembroId: string }) {
  const ahora = useAhora();
  const pref = usePref();
  const { avisos, citas, listo } = useAvisos(ahora, pref);
  const [leidos, setLeidos] = useLeidos(miembroId);
  const [abierto, setAbierto] = useState<DOMRect | null>(null);
  const [filtro, setFiltro] = useState<Filtro>('Todo');
  const [anim, setAnim] = useState(0);
  const boton = useRef<HTMLButtonElement>(null);
  const ir = useNavigate();

  const lista = useMemo(() => juntar([avisos], leidos), [avisos, leidos]);
  const nuevos = lista.filter((a) => esNuevo(a, leidos));
  const urgente = nuevos.some((a) => a.urgencia === 3);

  const leer = useCallback((ids: string[]) => setLeidos(marcar(leidos, ids, lista.map((a) => a.id))), [leidos, lista, setLeidos]);

  const abrir = useCallback(
    (a: Aviso) => {
      leer([a.id]);
      setAbierto(null);
      if (a.ir.a === 'correo') ir(`/bandeja/correos?hilo=${a.ir.hiloId}`);
      else if (a.ir.a === 'caso') ir(`/bandeja/casos?caso=${a.ir.casoId}`);
      else if (a.ir.a === 'ruta') ir(a.ir.ruta);
      else {
        const c = citas.get(a.ir.citaId);
        if (c) abrirDialogo({ tipo: 'cita', cita: c });
        else ir('/agenda');
      }
    },
    [leer, ir, citas],
  );
  const unirse = useCallback(
    (a: Aviso) => {
      leer([a.id]);
      if (a.enlace) window.open(a.enlace, '_blank', 'noopener');
    },
    [leer],
  );

  // Alertas: cada aviso nuevo suena y avisa una sola vez. Lo que ya estaba al abrir la Consola no suena (salvo una
  // reunion que empieza ya), para que recargar la pagina no sea un concierto.
  const alertados = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!listo) return;
    if (!alertados.current) {
      alertados.current = new Set(lista.filter((a) => !(a.tipo === 'reunion' && a.urgencia === 3)).map((a) => a.id));
    }
    const nuevosAhora = porAlertar(lista, leidos, alertados.current);
    if (!nuevosAhora.length) return;
    for (const a of nuevosAhora) alertados.current.add(a.id);
    setAnim((n) => n + 1);
    if (enNoMolestar(pref)) return;
    sonarAviso(sonidoDe(nuevosAhora[0], pref));
    const visible = document.visibilityState === 'visible' && document.hasFocus();
    for (const a of nuevosAhora.slice(0, 3)) {
      if (!visible && pref.notificar && 'Notification' in window && Notification.permission === 'granted') {
        const n = new Notification(a.titulo, { body: a.detalle, tag: a.id, requireInteraction: a.urgencia === 3 });
        n.onclick = () => {
          window.focus();
          abrir(a);
          n.close();
        };
      } else if (!(a.tipo === 'reunion' && a.urgencia === 3)) toast(`${a.titulo} · ${a.detalle}`);
    }
  }, [lista, leidos, listo, pref, abrir]);

  // Numero en el titulo de la pestaña.
  const base = useRef(document.title.replace(/^\(\d+\+?\)\s*/, ''));
  useEffect(() => {
    const n = nuevos.length;
    document.title = n ? `(${n > 9 ? '9+' : n}) ${base.current}` : base.current;
  }, [nuevos.length]);
  useEffect(() => () => void (document.title = base.current), []);

  useEffect(() => {
    if (!abierto) return;
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setAbierto(null);
        boton.current?.focus();
      }
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [abierto]);

  const vista = lista.filter((a) => (filtro === 'Todo' ? true : filtro === 'Sin leer' ? esNuevo(a, leidos) : DE_FILTRO[a.tipo] === filtro));
  const reunionYa = nuevos.find((a) => a.tipo === 'reunion' && a.urgencia === 3);
  const n = nuevos.length;

  return (
    <>
      <button
        ref={boton}
        className={`btn campana ${urgente ? 'urgente' : ''}`}
        key={anim}
        data-anim={anim ? 'si' : undefined}
        aria-label={n ? `Notificaciones, ${n} sin leer` : 'Notificaciones'}
        aria-haspopup="dialog"
        aria-expanded={!!abierto}
        onClick={(e) => setAbierto(abierto ? null : e.currentTarget.getBoundingClientRect())}
        data-testid="campana"
      >
        <Icon name="bell" />
        {n ? <span className="campana-n">{n > 9 ? '9+' : n}</span> : null}
      </button>
      {abierto
        ? createPortal(
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 69 }} onClick={() => setAbierto(null)} />
              <div className="campana-panel" role="dialog" aria-label="Notificaciones" style={{ top: abierto.bottom + 8, right: Math.max(8, window.innerWidth - abierto.right - 60) }}>
                <div className="campana-cab">
                  <h3>Notificaciones</h3>
                  <button className="linkish" onClick={() => leer(lista.map((a) => a.id))} disabled={!n}>
                    Marcar todo leído
                  </button>
                </div>
                <div className="campana-fil" role="group" aria-label="Filtrar">
                  {FILTROS.map((f) => (
                    <button key={f} aria-pressed={f === filtro} onClick={() => setFiltro(f)}>
                      {f}
                    </button>
                  ))}
                </div>
                <div className="campana-lista">
                  {vista.length ? (
                    GRUPOS.map(([g, t]) => {
                      const xs = vista.filter((a) => grupoDe(a, ahora) === g);
                      return xs.length ? (
                        <div key={g}>
                          <div className="campana-sec">{t}</div>
                          {xs.map((a) => (
                            <FilaAviso key={a.id} a={a} nuevo={esNuevo(a, leidos)} ahora={ahora} onAbrir={abrir} onUnirse={unirse} />
                          ))}
                        </div>
                      ) : null;
                    })
                  ) : (
                    <div className="campana-vacio">{listo ? 'Nada por aquí. Todo al día.' : 'Revisando…'}</div>
                  )}
                </div>
                <div className="campana-pie">
                  <span>Solo lo de tus buzones, tus casos y tu agenda</span>
                  <button
                    className="linkish"
                    onClick={() => {
                      setAbierto(null);
                      ir('/perfil#avisos');
                    }}
                  >
                    Preferencias
                  </button>
                </div>
              </div>
            </>,
            document.body,
          )
        : null}
      {reunionYa
        ? createPortal(
            <div className="reunion-ya" role="alert">
              <span className="reunion-ya-ico">
                <Icon name="video" />
              </span>
              <div className="reunion-ya-t">
                <b>{reunionYa.titulo}</b>
                <span>{reunionYa.detalle}</span>
              </div>
              {reunionYa.enlace ? (
                <button className="btn sm primary" onClick={() => unirse(reunionYa)}>
                  Unirse
                </button>
              ) : (
                <button className="btn sm primary" onClick={() => abrir(reunionYa)}>
                  Ver cita
                </button>
              )}
              <button className="btn sm ghost" onClick={() => leer([reunionYa.id])} aria-label="Descartar">
                Descartar
              </button>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function FilaAviso({ a, nuevo, ahora, onAbrir, onUnirse }: { a: Aviso; nuevo: boolean; ahora: number; onAbrir: (a: Aviso) => void; onUnirse: (a: Aviso) => void }) {
  const { deshacer, deshaciendo } = useDeshacerCopia();
  return (
    <div className={`campana-aviso t-${a.tipo} ${nuevo ? 'nuevo' : ''}`}>
      <button className="campana-aviso-b" onClick={() => onAbrir(a)}>
        <span className="campana-ico">
          <Icon name={ICONO[a.tipo]} />
        </span>
        <span className="campana-txt">
          <b>{a.titulo}</b>
          <span>{a.detalle}</span>
        </span>
        <span className="campana-hora">{horaCorta(a.fecha, ahora)}</span>
      </button>
      {a.tipo === 'reunion' && a.enlace ? (
        <div className="campana-acc">
          <button className="btn sm primary" onClick={() => onUnirse(a)}>
            Unirse
          </button>
          <button className="btn sm" onClick={() => onAbrir(a)}>
            Ver cita
          </button>
        </div>
      ) : null}
      {a.copia ? (
        <div className="campana-acc">
          <button className="btn sm" onClick={() => onAbrir(a)}>
            Ver el caso
          </button>
          <button className="btn sm ghost" disabled={!!deshaciendo} onClick={() => void deshacer(a.copia!)}>
            {deshaciendo ? 'Deshaciendo…' : 'Deshacer'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/* ---------------- Mi perfil -> Avisos ---------------- */
const hayNotificaciones = () => typeof window !== 'undefined' && 'Notification' in window;

export function MisAvisos() {
  const [p, setP] = useState(leerPref);
  const cambiar = (c: Partial<PrefNotif>) => {
    const n = { ...p, ...c };
    setP(n);
    guardarPref(n);
  };
  const fila = (titulo: string, sub: string, control: ReactNode) => (
    <div className="pref-fila">
      <div>
        <b>{titulo}</b>
        <small>{sub}</small>
      </div>
      {control}
    </div>
  );
  const sw = (k: 'correos' | 'casos' | 'invitaciones' | 'notificar' | 'sonido' | 'noMolestar', etiqueta: string) => (
    <input type="checkbox" className="pref-sw" role="switch" aria-label={etiqueta} checked={p[k]} onChange={(e) => cambiar({ [k]: e.target.checked })} />
  );
  return (
    <section className="box" id="avisos">
      <h2 className="lbl">Notificaciones</h2>
      <p className="sub" style={{ marginTop: 0 }}>
        La campana de arriba junta lo tuyo: correos de tus buzones, tus casos y tu agenda. Te avisa aunque estés en otra pantalla o pestaña.
      </p>
      <div className="pref-lista">
        {fila('Correos nuevos', 'De tu buzón y los de tus grupos', sw('correos', 'Correos nuevos'))}
        {fila('Clientes que responden y casos sin respuesta', 'Tus casos y los sin tomar de tus grupos', sw('casos', 'Casos'))}
        {fila(
          'Reuniones',
          'Avisar antes de empezar',
          <select className="inp" value={p.reuniones} onChange={(e) => cambiar({ reuniones: e.target.value as PrefNotif['reuniones'] })} aria-label="Avisar reuniones">
            <option value="15y5">15 y 5 min antes</option>
            <option value="5">Solo 5 min antes</option>
            <option value="no">No avisar</option>
          </select>,
        )}
        {fila('Invitaciones nuevas', 'Las que llegan por correo o por 3CX', sw('invitaciones', 'Invitaciones'))}
        {fila('Sonido', 'Corto y bajo; si llegan varios suena una vez', sw('sonido', 'Sonido'))}
        {p.sonido
          ? fila(
              'Qué suena',
              'Cada tipo tiene su tono',
              <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <select className="inp" value={p.modoSonido} onChange={(e) => cambiar({ modoSonido: e.target.value as PrefNotif['modoSonido'] })} aria-label="Qué suena">
                  <option value="tipo">El de cada tipo</option>
                  <option value="suave">Solo el suave</option>
                  <option value="reuniones">Solo reuniones</option>
                </select>
              </span>,
            )
          : null}
        {fila('Aviso del sistema', 'Notificación de Windows fuera del navegador', sw('notificar', 'Aviso del sistema'))}
        {/* Permiso del navegador: se explica primero y el cuadro del navegador sale solo al tocar Activar. */}
        {p.notificar && hayNotificaciones() ? <TarjetaPermiso tipo="notificaciones" compacta /> : null}
        {fila('No molestar', 'Sin sonido ni avisos; la campana sigue contando', sw('noMolestar', 'No molestar'))}
        {p.noMolestar
          ? fila(
              'Horario',
              'Puede pasar la medianoche',
              <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="time" className="inp" value={p.desde} onChange={(e) => cambiar({ desde: e.target.value })} aria-label="Desde" />a
                <input type="time" className="inp" value={p.hasta} onChange={(e) => cambiar({ hasta: e.target.value })} aria-label="Hasta" />
              </span>,
            )
          : null}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
        <span className="note" style={{ alignSelf: 'center' }}>
          Probar:
        </span>
        <button className="btn sm ghost" onClick={() => sonarAviso('suave')}>
          Suave
        </button>
        <button className="btn sm ghost" onClick={() => sonarAviso('campanilla')}>
          Campanilla
        </button>
        <button className="btn sm ghost" onClick={() => sonarAviso('reunion')}>
          Reunión ya
        </button>
      </div>
    </section>
  );
}
